import * as actionTypes from './types';
import * as authService from '../../service/auth';

import { cancelTokenRefresh, setupTokenRefresh } from '../../service/api';
import { ThunkAction } from 'redux-thunk';
import { Dispatch } from 'redux';
import { fetchCapabilities } from '../capabilities/actions';
import { fetchTree } from '../context/actions';
import { getOidcManager } from '../../service/auth/oidc';

// --- Define Redux Thunk Types ---
type RootState = any; // Replace with accurate type from your store if known
type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  any
>;

// --- Types ---
interface Workspace {
  id: string;
  name: string;
  [key: string]: any;
}

// --- Login Success Action ---
/**
 * Adopt a token payload that has already been fetched.
 *
 * `/login` is rate limited to 10/minute and now answers with one of a few bodies, so the
 * screens call it exactly once and read the result themselves. This takes the successful
 * payload from there and does the store-and-schedule half
 * that used to be buried inside the thunk below.
 */
export const loginSucceeded =
  (data: any): AppThunk =>
    async (dispatch: Dispatch) => {
      const auth_state = {
        current: data,
        isLoggedIn: true,
        isLoading: false,
        isSuccess: true
      };

      sessionStorage.setItem('auth', JSON.stringify(auth_state));
      localStorage.removeItem('isLogout');
      setupTokenRefresh();
      dispatch({
        type: actionTypes.REQUEST_SUCCESS,
        payload: data
      });
    };

// --- Login Action ---
export const login =
  ({
    email,
    password,
    otp
  }: {
    email: string;
    password: string;
    otp?: string;
  }): AppThunk =>
    async (dispatch: Dispatch) => {
      dispatch({ type: actionTypes.REQUEST_LOADING });

      try {
        const response = await authService.login({ email, password, otp });
        if (
          'status' in response &&
        response.status === 200 &&
        response.data?.access_token
        ) {
          await (loginSucceeded(response.data) as any)(dispatch);
        } else {
        // A body without a token is a two-factor challenge, not a session. Callers that can
        // present a challenge screen call the service directly; this thunk cannot, so it
        // reports failure rather than silently storing a token-less payload as a session.
          dispatch({ type: actionTypes.REQUEST_FAILED });
        }
      } catch (error) {
        dispatch({ type: actionTypes.REQUEST_FAILED });
      }
    };

// --- Logout Action ---
/**
 * End the session: locally first, then at the server.
 *
 * The order is the whole fix for a sign-out that looped straight back into the app. This used
 * to `await` the API call and clear storage *afterwards*, while callers navigated to `/login`
 * in the same tick without awaiting the thunk. `LoginForm` then still saw the previous
 * session's `isSuccess` in the store, called `window.location.replace('/')`, and tore the page
 * down before the `await` ever resumed, so `auth` was never removed, and the reloaded app
 * found it in storage and signed straight back in.
 *
 * Clearing synchronously, before the request, makes every call site correct whether or not it
 * awaits: by the time `navigate('/login')` runs on the next line of any caller, the store and
 * storage are already empty. The server call is best-effort after that, and a failure can no
 * longer strand someone in a session they asked to leave.
 */
export const logout = (): AppThunk => async (dispatch: Dispatch) => {
  // Read before clearing: the request needs both tokens, and nothing else will have them.
  let tokens: {
    refreshToken?: string;
    accessToken?: string;
    idToken?: string;
  } = {};
  try {
    const stored = sessionStorage.getItem('auth');
    const current = stored ? JSON.parse(stored)?.current : null;
    tokens = {
      refreshToken: current?.refresh_token,
      accessToken: current?.access_token,
      idToken: current?.id_token
    };
  } catch {
    // Unparseable storage is still a session to leave. Fall through and clear it.
  }

  // The scheduled refresh outlives the session otherwise, and fires into an empty storage a
  // few minutes later, which raises a "Session Expired" popup at someone who signed out on
  // purpose.
  cancelTokenRefresh();

  sessionStorage.removeItem('auth');
  dispatch({ type: actionTypes.LOGOUT_SUCCESS });

  // Lets the login page say the sign-out happened, rather than showing a bare sign-in button.
  sessionStorage.setItem('signedOut', '1');

  try {
    const manager = getOidcManager();
    if (tokens.idToken) {
      await manager.signoutRedirect({ id_token_hint: tokens.idToken });
    } else {
      // Without an id token Keycloak cannot trust the post-logout redirect on its own; naming
      // the client lets it validate the redirect instead of showing its own logout page.
      await manager.signoutRedirect({
        extraQueryParams: {
          client_id: manager.settings.client_id,
          post_logout_redirect_uri: manager.settings.post_logout_redirect_uri || ''
        }
      });
    }
  } catch (error) {
    // Not a failed sign-out from where the user is standing: they are already out locally.
    // It is a session left alive at Keycloak, which is worth logging and worth not raising
    // into a blocking error on a screen the user has already left.
    console.error('Error starting Keycloak sign-out:', error);
    dispatch({ type: actionTypes.LOGOUT_FAILED });
  }
};

// --- Fetch User Action ---
export const fetchUser =
  (): AppThunk<Promise<{ 1: number } | undefined>> =>
    async (dispatch: Dispatch) => {
      dispatch({ type: actionTypes.REQUEST_LOADING });

      try {
        const existingAuthState = JSON.parse(
          sessionStorage.getItem('auth') || '{}'
        );
        const localWorkspaceId = existingAuthState.current?.default_workspace?.id;
        const response = await authService.getMe(
          localWorkspaceId ? String(localWorkspaceId) : undefined
        );

        if ('status' in response && response.status === 200) {
          const { data } = response;
          const { user } = data;

          let combinedPermissions: string[] = [];
          if (data.default_workspace?.id != null) {
          // A membership row carries `{ role: 'ADMIN' }` and no `permissions` key: the
          // capability surface replaced that list. Without the guard this produced
          // `[undefined]`, which reads as "one permission" everywhere it is length-checked.
            combinedPermissions = (data.default_workspace?.roles ?? []).flatMap(
              (role: any) => role.permissions ?? []
            );
          }

          // `/users/me/details` has no `user.roles`; the caller's role lives on the membership
          // row. Reading the missing field left `roles` undefined, which is why the profile
          // showed an empty role banner and its form could never become valid.
          const workspaceRoles: string[] = (data.default_workspace?.roles ?? [])
            .map((role: any) => role?.role ?? role?.name)
            .filter(Boolean);

          const userInfo = {
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            roles: user.roles ?? workspaceRoles,
            platformRoles: user.platform_roles ?? [],
            userName: user.userName,
            // Read as `currentUser.keycloak_user_id` by the assignment and activity screens.
            keycloak_user_id: user.keycloak_user_id,
            default_workspace: data.default_workspace,
            permissions: combinedPermissions,
            workspaces: data.workspaces,
            admin_kc: user.is_keycloak_user_admin,
            home_node_id: user.home_node_id ?? null
          };

          if (
            localWorkspaceId &&
          String(localWorkspaceId) === String(data.default_workspace?.id)
          ) {
          // The backend always returns active_workspace_id's workspace in default_workspace
          // regardless of the workspace_id param. Only the roles are scoped to our request.
          // Preserve the locally selected workspace object and just refresh its roles.
            userInfo.default_workspace = {
              ...existingAuthState.current.default_workspace,
              roles:
              data.default_workspace?.roles ??
              existingAuthState.current.default_workspace?.roles ??
              []
            };
            userInfo.permissions = (
            userInfo.default_workspace.roles as any[]
            ).flatMap((role: any) => role.permissions ?? []);
            userInfo.roles = (userInfo.default_workspace.roles as any[])
              .map((role: any) => role?.role ?? role?.name)
              .filter(Boolean);
          } else if (data.default_workspace?.id != null) {
          // No locally selected workspace. Server's active_workspace_id is the truth.
          // Initialise the server-default tracker so the "Set Default" checkbox is correct.
            localStorage.setItem(
              'server_default_ws_id',
              String(data.default_workspace.id)
            );
          } else {
            localStorage.removeItem('server_default_ws_id');
          }

          const updatedAuthState = {
            ...existingAuthState,
            current: {
              ...existingAuthState.current,
              ...userInfo
            },
            isLoggedIn: true,
            isLoading: false,
            isSuccess: true
          };

          sessionStorage.setItem('auth', JSON.stringify(updatedAuthState));

          dispatch({ type: actionTypes.REQUEST_SUCCESS, payload: userInfo });

          const activeWorkspaceId = userInfo.default_workspace?.id;
          if (activeWorkspaceId != null) {
            (dispatch as any)(fetchCapabilities(activeWorkspaceId));
          }
          (dispatch as any)(fetchTree());

          return { 1: 1 };
        } else {
          dispatch({ type: actionTypes.REQUEST_FAILED });
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        dispatch({ type: actionTypes.REQUEST_FAILED });
      }
    };

// --- Update Default Workspace Action ---
// `null` is a valid payload: after deleting the active workspace the backend leaves the
// user with no active workspace and does not pick a replacement for them.
export const updateDefaultWorkspace =
  (workspace: Workspace | null): AppThunk =>
    async (dispatch: Dispatch) => {
      try {
        dispatch({
          type: actionTypes.UPDATE_WORKSPACE_SELECT,
          payload: workspace
        });
      } catch (error) {
        console.error('Error updating default workspace:', error);
      }
    };
